/**
 * Spec 014b (analysis C1): a car as this site's brand rents it. `GET_CAR`
 * returns the host's own values (for the host's edit form) and `tripSettings`,
 * the values trips on this site use. Guest pages read the car through
 * `carAsRented`, so they show what the server will charge and check. Tested in
 * `carTripSettings.test.ts`.
 */

export interface CarTripSettings {
  hourlyRentalOffered: boolean;
  monthlyRentalOffered: boolean;
  discount?: Record<string, unknown> | null;
  protectionDeclinable: boolean;
  minimumAge: number;
  advanceNoticeHours: number;
  tripBufferHours: number;
  minimumRentalDays?: number | null;
  maximumRentalDays?: number | null;
  unlimitedMileage: boolean;
  milesPerDay: number;
  overagePerMile?: number | null;
  deposit: number;
  guestInstructions?: string | null;
}

type RentableCar = {
  tripSettings?: CarTripSettings | null;
  distanceLimit?: Record<string, unknown> | null;
  tripPreferences?: Record<string, unknown> | null;
  discount?: Record<string, unknown> | null;
};

/** The car with the brand's trip values in place of the host's; unchanged without `tripSettings`. */
export function carAsRented<T extends RentableCar>(car: T): T;
export function carAsRented<T extends RentableCar>(car: T | null | undefined): T | null | undefined;
export function carAsRented<T extends RentableCar>(car: T | null | undefined) {
  const settings = car?.tripSettings;
  if (!car || !settings) return car;
  return {
    ...car,
    hourlyRentalIsOn: settings.hourlyRentalOffered,
    monthlyRentalIsOn: settings.monthlyRentalOffered,
    discount: settings.discount ? { ...(car.discount ?? {}), ...settings.discount } : car.discount,
    isProtectionDeclinable: settings.protectionDeclinable,
    ageLimit: settings.minimumAge,
    tripPreferences: {
      ...(car.tripPreferences ?? {}),
      advanceNoticeHours: settings.advanceNoticeHours,
      tripBufferHours: settings.tripBufferHours,
    },
    minimumRentalDays: settings.minimumRentalDays ?? null,
    maximumRentalDays: settings.maximumRentalDays ?? null,
    distanceLimit: {
      ...(car.distanceLimit ?? {}),
      dailyDistanceLimit: settings.milesPerDay,
      unlimitedMileage: settings.unlimitedMileage,
    },
    deposit: settings.deposit,
    guestInstructions: settings.guestInstructions ?? null,
  };
}
